
import React, { useEffect, useState } from 'react';
import { Template, UserProfile, UserRole } from '@/shared/types';
import { Button } from '@/shared/components/ui';
import { dataService } from '@/shared/services/dataService';
import { realDataService } from '@/shared/services/realDataService';
import { LayoutTemplate, ToggleLeft, ToggleRight, Star, Plus, Eye, ShieldCheck, X, RefreshCw } from 'lucide-react';
import { PDFLayout } from '@/shared/components/PDFLayout';
import AdminTemplatesSkeleton from '@/features/admin/components/AdminTemplatesSkeleton';

const AdminTemplatesView: React.FC = () => {
    const [templates, setTemplates] = useState<Template[]>([]);
    const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
    const [previewScale, setPreviewScale] = useState(1);
    const [loading, setLoading] = useState(true);
    const [previewProfile, setPreviewProfile] = useState<UserProfile | null>(null);

    // Initialize preview profile
    useEffect(() => {
        setPreviewProfile({
            id: 'preview-admin',
            userId: 'preview-admin',
            name: "Acme Innovations",
            email: "hello@acme.inc",
            role: UserRole.FREELANCER,
            businessName: "Acme Innovations",
            businessAddress: "123 Innovation Dr, Tech City",
            businessPhone: "+1 (555) 0199-2834",
            brandColor: '#4f46e5',
            template: 'classic' as any,
            subscription: {
                planId: 'pro',
                status: 'active',
                billingPeriod: 'monthly',
                nextBillingDate: new Date().toISOString()
            },
            country: 'US',
            currency: 'USD' as any,
            savedServices: [],
            paymentMethods: [],
            joinedAt: new Date().toISOString(),
            notificationPreferences: {
                email: true,
                push: true,
                sms: false
            } as any
        });
    }, []);

    const demoInvoiceItems = [
        { description: 'Web Development Services', amount: 2500.00 },
        { description: 'UI/UX Design', amount: 1200.00 },
    ];

    const demoQuoteItems = [
        { description: 'Initial Consultation', amount: 250.00 },
        { description: 'Project Planning', amount: 500.00 },
    ];

    useEffect(() => {
        const loadTemplates = async () => {
            try {
                const allTemplates = await realDataService.getTemplates();
                setTemplates(allTemplates);
            } catch (error) {
                console.error('Failed to load templates:', error);
                setTemplates(dataService.getTemplates());
            }
            setLoading(false);
        };

        loadTemplates();
    }, []);

    const handleToggleActive = (id: string) => {
        console.log('Toggle template status:', id);
    };

    const handleTogglePremium = (template: Template) => {
        console.log('Toggle template premium:', template.id);
    };

    // Mini Template Preview Component for Cards - Shows a snapshot of the design
    const TemplateMiniPreview: React.FC<{ template: Template }> = ({ template }) => {
        const [localProfile, setLocalProfile] = useState<UserProfile | null>(null);
        const [miniScale, setMiniScale] = useState(0.18);

        useEffect(() => {
            if (previewProfile) {
                // Update the profile's template when the template prop changes
                setLocalProfile({ ...previewProfile, template: template.code });
            }
        }, [template, previewProfile]);

        if (!localProfile) return null;

        return (
            <div className="w-full h-full bg-white relative overflow-hidden">
                <div
                    className="origin-top-left w-[1600px]"
                    style={{ transform: `scale(${miniScale})` }}
                >
                    <PDFLayout
                        profile={localProfile}
                        title="Invoice"
                        type="Invoice"
                        metaData={[]}
                        client={{
                            id: 'demo',
                            userId: 'preview-admin',
                            name: 'Global Corp',
                            company: 'Global Corp International',
                            email: 'billing@globalcorp.com',
                            phone: '+44 20 7123 4567',
                            address: '42 Business Square'
                        }}
                        footerNote=""
                    >
                        <div className="-m-8">
                            <div className="p-4">
                                <h3 className="text-xl font-bold text-gray-900 mb-1">{localProfile.businessName}</h3>
                                <p className="text-sm text-gray-600 mb-3">{localProfile.businessAddress}</p>
                                <div className="flex justify-between items-center mb-3">
                                    <span className="text-sm font-semibold text-gray-800">Invoice #INV-001</span>
                                    <span className="text-sm text-gray-500">{new Date().toLocaleDateString()}</span>
                                </div>
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-gray-300 text-xs text-gray-500 uppercase">
                                            <th className="py-1 text-left">Description</th>
                                            <th className="py-1 text-right">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-gray-700 text-xs">
                                        {demoInvoiceItems.map((item, i) => (
                                            <tr key={i} className="border-b border-gray-100">
                                                <td className="py-1">{item.description}</td>
                                                <td className="py-1 text-right">${item.amount.toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </PDFLayout>
                </div>

                {/* Scale Controls */}
                <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-white/90 backdrop-blur rounded border border-gray-200 px-1.5 py-1 shadow-sm">
                    <button
                        onClick={() => setMiniScale(s => Math.max(0.1, s - 0.02))}
                        className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded text-xs font-medium"
                    >
                        -
                    </button>
                    <span className="text-[10px] text-gray-600 font-medium min-w-[35px] text-center">
                        {Math.round(miniScale * 100)}%
                    </span>
                    <button
                        onClick={() => setMiniScale(s => Math.min(0.3, s + 0.02))}
                        className="w-6 h-6 flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded text-xs font-medium"
                    >
                        +
                    </button>
                </div>
            </div>
        );
    };

    const TemplatePreviewModal = ({ template, onClose }: { template: Template; onClose: () => void }) => {
        const [documentType, setDocumentType] = useState<'invoice' | 'quote'>('invoice');
        const [renderKey, setRenderKey] = useState(0);
        const [modalProfile, setModalProfile] = useState<UserProfile | null>(null);

        // Update modal profile when template changes
        useEffect(() => {
            if (previewProfile) {
                setModalProfile({ ...previewProfile, template: template.code });
            }
        }, [template, previewProfile]);

        const handleRefresh = () => {
            setRenderKey(prev => prev + 1);
        };

        if (!modalProfile) return null;

        return (
            <div className="fixed inset-0 z-[100] bg-gray-900/90 backdrop-blur-md flex flex-col">
                {/* Modal Header */}
                <div className="bg-white border-b border-gray-200 p-4 flex justify-between items-center shadow-sm z-50">
                    <div className="flex items-center gap-3">
                        <div className="bg-primary-100 text-primary-700 p-2 rounded">
                            <LayoutTemplate size={16} />
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900 text-sm">{template.name}</h3>
                            <div className="flex items-center gap-2 text-xs text-gray-500 font-mono">
                                <span>Template: {template.code}</span>
                                {template.isPremium && <span className="text-primary-600 font-medium bg-primary-50 px-2 py-0.5 rounded text-xs">PREMIUM</span>}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden md:flex items-center gap-1.5 text-xs text-gray-400 font-medium uppercase bg-gray-100 px-2 py-1 rounded">
                            <ShieldCheck size={12} /> Preview Mode
                        </div>
                        <button
                            onClick={handleRefresh}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="Refresh preview"
                        >
                            <RefreshCw size={14} />
                        </button>
                        <button
                            onClick={() => setPreviewScale(s => Math.max(0.5, s - 0.1))}
                            className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded text-sm font-medium transition-colors"
                        >
                            Zoom Out
                        </button>
                        <span className="text-xs text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded">
                            {Math.round(previewScale * 100)}%
                        </span>
                        <button
                            onClick={() => setPreviewScale(s => Math.min(1.5, s + 0.1))}
                            className="px-3 py-2 text-gray-600 hover:bg-gray-100 rounded text-sm font-medium transition-colors"
                        >
                            Zoom In
                        </button>
                        <button
                            onClick={onClose}
                            className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded font-medium transition-colors flex items-center gap-2"
                        >
                            <X size={14} /> Close
                        </button>
                    </div>
                </div>

                {/* Document Type Selector */}
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <span className="text-xs font-medium text-gray-500 uppercase">Preview Document:</span>
                        <div className="flex gap-2">
                            <button
                                onClick={() => { setDocumentType('invoice'); setRenderKey(prev => prev + 1); }}
                                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                                    documentType === 'invoice'
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                                }`}
                            >
                                Invoice
                            </button>
                            <button
                                onClick={() => { setDocumentType('quote'); setRenderKey(prev => prev + 1); }}
                                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                                    documentType === 'quote'
                                        ? 'bg-gray-900 text-white'
                                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-300'
                                }`}
                            >
                                Quote
                            </button>
                        </div>
                    </div>
                </div>

                {/* Preview Area */}
                <div className="flex-1 overflow-y-auto p-6 flex justify-center bg-gray-800">
                    <div
                        className="origin-top transition-transform duration-200"
                        style={{ transform: `scale(${previewScale})` }}
                    >
                        <div key={renderKey}>
                            <PDFLayout
                                profile={modalProfile}
                                title={documentType === 'invoice' ? 'Invoice' : 'Quote'}
                                type={documentType === 'invoice' ? 'Invoice' : 'Quote'}
                                metaData={[
                                    { label: `${documentType === 'invoice' ? 'Invoice' : 'Quote'} #`, value: documentType === 'invoice' ? 'INV-2024-001' : 'QT-2024-001' },
                                    { label: 'Date', value: new Date().toLocaleDateString() },
                                    { label: 'Due Date', value: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString() },
                                    { label: 'Amount', value: documentType === 'invoice' ? '$4,950.00' : '$3,000.00' }
                                ]}
                                client={{
                                    id: 'demo',
                                    userId: 'preview-admin',
                                    name: 'Global Corp Ltd.',
                                    company: 'Global Corp International',
                                    email: 'billing@globalcorp.com',
                                    phone: '+44 20 7123 4567',
                                    address: '42 Business Square, London, UK'
                                }}
                                footerNote="This is a live preview of the template with sample data."
                            >
                                <div className="mt-6">
                                    <table className="w-full text-left">
                                        <thead>
                                            <tr className="border-b-2 border-gray-200 text-gray-500 font-medium uppercase text-xs tracking-wider">
                                                <th className="py-3">Description</th>
                                                <th className="py-3 text-right">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-700 text-sm">
                                            {(documentType === 'invoice' ? demoInvoiceItems : demoQuoteItems).map((item, i) => (
                                                <tr key={i} className="border-b border-gray-100">
                                                    <td className="py-3 font-medium">{item.description}</td>
                                                    <td className="py-3 text-right font-mono">${item.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr className="text-base font-semibold text-gray-900 border-t-2 border-gray-900">
                                                <td className="py-3 text-right">Total</td>
                                                <td className="py-3 text-right font-mono">
                                                    ${(documentType === 'invoice' ? demoInvoiceItems : demoQuoteItems)
                                                        .reduce((sum, item) => sum + item.amount, 0)
                                                        .toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </PDFLayout>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return <AdminTemplatesSkeleton />;
    }

    return (
        <div className="space-y-5 max-w-7xl mx-auto">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Template Manager</h2>
                    <p className="text-gray-500 text-sm">Manage, deploy, and preview document layouts.</p>
                </div>
                <div className="flex gap-3">
                    <button
                        disabled
                        className="bg-gray-200 text-gray-400 px-4 py-2 rounded font-medium flex items-center gap-2 cursor-not-allowed text-sm"
                    >
                        <Plus size={14}/> Upload Template (JSON)
                    </button>
                </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {templates.map(template => (
                    <div key={template.id} className="bg-white rounded border border-gray-200 overflow-hidden flex flex-col group hover:border-gray-300 transition-colors">
                        {/* Status Bar */}
                        <div className="px-3 py-2 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <span className={`text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded ${
                                template.isActive
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-gray-200 text-gray-500'
                            }`}>
                                {template.isActive ? 'Live' : 'Inactive'}
                            </span>
                            {template.isPremium && (
                                <span className="text-[10px] font-medium uppercase text-primary-600 flex items-center gap-1">
                                    <Star size={10} fill="currentColor"/> Premium
                                </span>
                            )}
                        </div>

                        {/* Actual Template Preview Thumbnail - Shows a snapshot of the design */}
                        <div className="h-40 bg-gray-50 relative overflow-hidden">
                            <TemplateMiniPreview template={template} />

                            {/* Hover Overlay */}
                            <div className="absolute inset-0 bg-gray-900/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                <button
                                    onClick={() => setPreviewTemplate(template)}
                                    className="bg-white text-gray-900 px-4 py-2 rounded font-medium flex items-center gap-2 shadow hover:bg-gray-50 transition-colors text-sm"
                                >
                                    <Eye size={14}/> Preview
                                </button>
                            </div>
                        </div>

                        <div className="p-4 flex-1 flex flex-col">
                            <h3 className="font-medium text-gray-900 text-sm mb-1">{template.name}</h3>
                            <p className="text-gray-500 text-xs mb-4 font-mono bg-gray-50 inline-block px-1.5 py-0.5 rounded">{template.code}</p>

                            {/* Stats Mockup */}
                            <div className="grid grid-cols-2 gap-2 mb-4">
                                <div className="text-center p-2 bg-gray-50 rounded">
                                    <p className="text-[10px] text-gray-400 uppercase font-medium">Usage</p>
                                    <p className="font-medium text-gray-700 text-sm">{Math.floor(Math.random() * 500) + 50}</p>
                                </div>
                                <div className="text-center p-2 bg-gray-50 rounded">
                                    <p className="text-[10px] text-gray-400 uppercase font-medium">Rating</p>
                                    <p className="font-medium text-gray-700 text-sm">4.{Math.floor(Math.random() * 9)}</p>
                                </div>
                            </div>

                            <div className="mt-auto space-y-2 pt-3 border-t border-gray-100">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-gray-600">Active</span>
                                    <button
                                        onClick={() => handleToggleActive(template.id)}
                                        className={`transition-colors ${template.isActive ? 'text-emerald-600' : 'text-gray-300 hover:text-gray-500'}`}
                                        title="Toggle Availability"
                                    >
                                        {template.isActive ? <ToggleRight size={20}/> : <ToggleLeft size={20}/>}
                                    </button>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-medium text-gray-600">Pro Plan</span>
                                    <button
                                        onClick={() => handleTogglePremium(template)}
                                        className={`transition-colors ${template.isPremium ? 'text-primary-600' : 'text-gray-300 hover:text-gray-500'}`}
                                        title="Toggle Premium Status"
                                    >
                                        {template.isPremium ? <ToggleRight size={20}/> : <ToggleLeft size={20}/>}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Preview Modal */}
            {previewTemplate && (
                <TemplatePreviewModal
                    template={previewTemplate}
                    onClose={() => setPreviewTemplate(null)}
                />
            )}
        </div>
    );
};

export default AdminTemplatesView;
